import {
  Box,
  LinearProgress,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  RequestParticipant,
  SignRequest,
  SortingConfig,
} from "models/interfaces";
import { fetchSentRequest } from "screens/MySignRequests/MySignRequestService";
import {
  convertESTtoUserLocalDateAndTime,
  handleErrorMessage,
  splitByCamelCase,
} from "helpers/methods";
import EmptyState from "global/components/EmptyState/EmptyState";
import strings from "global/constants/StringConstants";
import viewpaths from "global/constants/ViewPathConstants";
import history from "utils/history";
import { hasAccessTo } from "layouts/Config";
import { useAppDispatch } from "redux/hooks";
import { setReduxSignRequest } from "redux/appSlice";
import {
  dashFont,
  dashRef,
  getActivityStatusStyle,
} from "./DashboardReferenceStyles";

const SORTING: SortingConfig[] = [
  { entity: "signRequest", order: "DESC", field: "createdOn" },
];

type Props = {
  /** Rows to show; the reference lists four. */
  rows?: number;
};

/**
 * Recent activity for the dashboard only. The Sent/Inbox screens keep using
 * `MySignRequestsList` — this one owns its own fetch and renders the compact
 * row layout from the dashboard reference.
 */
const DashboardRecentActivity = ({ rows = 4 }: Props) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  // From `md` up the card shares its row with the renewals chart, so the
  // status pill sits above the progress bar in a narrow right-hand column.
  // Below that the card is full width and the reference lays the row out
  // flat — inline on the tablet, wrapped on the phone.
  const isNarrowColumn = useMediaQuery(theme.breakpoints.up("md"));
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState<boolean>(true);
  const [requests, setRequests] = useState<SignRequest[]>([]);

  const canOpenRequest = hasAccessTo(
    strings.resources.sign,
    strings.fetchPermission,
  );

  useEffect(() => {
    const fetchRecentRequests = async () => {
      try {
        setLoading(true);
        const data = await fetchSentRequest(1, SORTING, "All", "", "");
        setRequests(((data ?? []) as SignRequest[]).slice(0, rows));
      } catch (error: any) {
        handleErrorMessage(error);
      } finally {
        setLoading(false);
      }
    };
    fetchRecentRequests();
  }, [rows]);

  // Delegated participants and cc recipients don't sign, so they're outside
  // the ratio — same rule the Sent list applies.
  const getProgress = (signers: RequestParticipant[] = []) => {
    const completedCount = signers.filter(
      (signer) => signer.status === strings.signed && !signer.cc,
    ).length;
    const totalCount = signers.filter(
      (signer) => signer.status !== strings.Delegated && !signer.cc,
    ).length;
    return {
      completedCount,
      totalCount,
      percent: totalCount > 0 ? (completedCount / totalCount) * 100 : 0,
    };
  };

  const openRequest = (request: SignRequest) => {
    if (request.status === strings.draft) {
      history.push({ pathname: viewpaths.editDraftViewPath, state: request });
      return;
    }
    if (canOpenRequest) {
      history.push(`${viewpaths.viewRequestViewPath}/${request.id}`);
    }
  };

  // The phone reference lists three rows rather than four. The fetch is left
  // alone so a rotation never re-hits the API — only the slice shown changes.
  const visibleRows = isPhone ? Math.min(rows, 3) : rows;
  const visibleRequests = requests.slice(0, visibleRows);

  // Pill and progress bar are identical at every width — only where they sit
  // in the row changes, so each takes the placement rules from its caller.
  // The extra rules never touch a key the base sets, so a plain spread is safe.
  /**
   * A text `Skeleton` sizes its box from `fontSize` x line-height, but inherits
   * `normal` (~1.2) where `Typography` applies MUI's 1.5. Without this the
   * placeholders sit ~3px short of the text they stand in for.
   */
  const TEXT_LINE_HEIGHT = 1.5;

  /**
   * Geometry shared by a real row and its skeleton, so the list cannot jump
   * when the data lands. Everything interactive (cursor, hover, focus ring)
   * stays on the real row.
   */
  const rowChromeSx = (isLast: boolean) => ({
    display: "flex",
    flexWrap: isNarrowColumn ? "nowrap" : { xs: "wrap", sm: "nowrap" },
    alignItems: isNarrowColumn ? "center" : { xs: "flex-start", sm: "center" },
    columnGap: { xs: 1.125, sm: 1.75 },
    rowGap: 1,
    // Below `md` the reference runs rows edge to edge inside the card
    // padding; the desktop column keeps its 8px inset.
    padding: { xs: "12px 0", sm: "13px 0", md: "13px 8px" },
    borderRadius: "12px",
    // Below `md` the reference rules each row off at the top, so the list is
    // separated from the card title as well. The desktop column keeps its
    // bottom rules between rows only.
    borderTop: isNarrowColumn ? "none" : `1px solid ${dashRef.rowDivider}`,
    borderBottom:
      isNarrowColumn && !isLast ? `1px solid ${dashRef.rowDivider}` : "none",
  });

  const renderStatusPill = (
    statusStyle: { color: string; bg: string },
    status: string,
    placementSx: Record<string, any> = {},
  ) => (
    <Box
      component="span"
      sx={{
        fontSize: dashFont.statusPill,
        fontWeight: 700,
        letterSpacing: "0.04em",
        color: statusStyle.color,
        backgroundColor: statusStyle.bg,
        borderRadius: "999px",
        padding: { xs: "4px 9px", sm: "4px 11px" },
        whiteSpace: "nowrap",
        flexShrink: 0,
        ...placementSx,
      }}
    >
      {splitByCamelCase(status).toUpperCase()}
    </Box>
  );

  const renderProgress = (
    completedCount: number,
    totalCount: number,
    percent: number,
    options: {
      placementSx?: Record<string, any>;
      /** Phone gives the bar a row of its own, so the track stretches. */
      fillOnPhone?: boolean;
    } = {},
  ) => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.875}
      sx={{ flexShrink: 0, ...(options.placementSx ?? {}) }}
    >
      <LinearProgress
        variant="determinate"
        value={percent}
        aria-label={`${completedCount} of ${totalCount} signed`}
        sx={{
          flex: options.fillOnPhone ? { xs: 1, sm: "0 0 auto" } : "0 0 auto",
          width: options.fillOnPhone ? { xs: "auto", sm: 64 } : 64,
          height: 5,
          borderRadius: "3px",
          backgroundColor: dashRef.progressTrack,
          "& .MuiLinearProgress-bar": {
            borderRadius: "3px",
            backgroundColor: dashRef.progressFill,
          },
        }}
      />
      <Typography
        sx={{
          fontSize: dashFont.progressLabel,
          fontWeight: 600,
          color: dashRef.metaText,
          whiteSpace: "nowrap",
        }}
      >
        {completedCount}/{totalCount}
      </Typography>
    </Stack>
  );

  const renderRow = (request: SignRequest, index: number) => {
    const { completedCount, totalCount, percent } = getProgress(
      request.signers,
    );
    const statusStyle = getActivityStatusStyle(request.status);
    const isLast = index === visibleRequests.length - 1;
    const createdBy = request.requestedBy?.name ?? "—";
    const createdOn = convertESTtoUserLocalDateAndTime(
      request.createdOn,
      "MMM D, h:mm A",
    );

    return (
      <Box
        key={request.id || index}
        role="button"
        tabIndex={0}
        onClick={() => openRequest(request)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openRequest(request);
          }
        }}
        sx={{
          ...rowChromeSx(isLast),
          cursor: "pointer",
          transition: "background-color 0.15s ease",
          "&:hover": { backgroundColor: dashRef.rowHover },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: "-2px",
          },
        }}
      >
        {/* `flex: 1` is basis-0 on purpose: with a content-sized basis a long
            subject measures wider than the card and pushes the pill onto its
            own wrapped line instead of keeping it beside the title. */}
        <Stack spacing={0.375} sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip title={request.emailSubject} arrow describeChild>
            <Typography
              sx={{
                fontSize: dashFont.rowTitle,
                fontWeight: 700,
                color: dashRef.rowTitle,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {request.emailSubject}
            </Typography>
          </Tooltip>
          <Typography
            sx={{
              fontSize: dashFont.rowMeta,
              color: dashRef.metaText,
              // Below `md` the pill and bar share the line, so the meta has to
              // give way rather than wrap. The desktop column keeps wrapping.
              whiteSpace: { xs: "nowrap", md: "normal" },
              overflow: { xs: "hidden", md: "visible" },
              textOverflow: { xs: "ellipsis", md: "clip" },
            }}
          >
            {`Created by ${createdBy} · ${createdOn}`}
          </Typography>
        </Stack>

        {isNarrowColumn ? (
          <Stack spacing={0.75} alignItems="flex-end" sx={{ flexShrink: 0 }}>
            {renderStatusPill(statusStyle, request.status)}
            {renderProgress(completedCount, totalCount, percent)}
          </Stack>
        ) : (
          <>
            {/* Phone puts the pill on the title's line and drops the bar to a
                row of its own; the tablet keeps all three inline, bar first. */}
            {renderStatusPill(statusStyle, request.status, {
              order: { xs: 2, sm: 3 },
              // Reference pins the tablet pill to a common width so the column
              // lines up; `minWidth` keeps a long status from spilling out.
              minWidth: { sm: 104 },
              textAlign: "center",
            })}
            {renderProgress(completedCount, totalCount, percent, {
              placementSx: {
                order: { xs: 3, sm: 2 },
                flexBasis: { xs: "100%", sm: "auto" },
              },
              fillOnPhone: true,
            })}
          </>
        )}
      </Box>
    );
  };

  /**
   * Mirrors `renderRow` part for part. The text placeholders are sized by
   * `fontSize` rather than a fixed pixel height so each one occupies the same
   * line box as the text it stands in for — a hard-coded height made the
   * skeleton row 65px against the real row's 72px, so the whole card jumped
   * when the request list arrived.
   */
  const renderSkeletonPill = (placementSx: Record<string, any> = {}) => (
    <Skeleton
      variant="rounded"
      sx={{
        height: 22,
        width: 80,
        borderRadius: "999px",
        flexShrink: 0,
        ...placementSx,
      }}
    />
  );

  const renderSkeletonProgress = (placementSx: Record<string, any> = {}) => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.875}
      sx={{ flexShrink: 0, ...placementSx }}
    >
      <Skeleton
        variant="rounded"
        sx={{ width: 64, height: 5, borderRadius: "3px", flexShrink: 0 }}
      />
      {/* Stands in for the "0/1" tally, and sets the row's height as it does. */}
      <Skeleton
        variant="text"
        sx={{
          fontSize: dashFont.progressLabel,
          lineHeight: TEXT_LINE_HEIGHT,
          width: 22,
          flexShrink: 0,
        }}
      />
    </Stack>
  );

  const renderSkeletonRow = (index: number) => (
    <Box
      key={`activity-skeleton-${index}`}
      aria-hidden="true"
      sx={rowChromeSx(index === visibleRows - 1)}
    >
      <Stack spacing={0.375} sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton
          variant="text"
          sx={{
            fontSize: dashFont.rowTitle,
            lineHeight: TEXT_LINE_HEIGHT,
            width: "70%",
          }}
        />
        <Skeleton
          variant="text"
          sx={{
            fontSize: dashFont.rowMeta,
            lineHeight: TEXT_LINE_HEIGHT,
            width: "45%",
          }}
        />
      </Stack>

      {isNarrowColumn ? (
        <Stack spacing={0.75} alignItems="flex-end" sx={{ flexShrink: 0 }}>
          {renderSkeletonPill()}
          {renderSkeletonProgress()}
        </Stack>
      ) : (
        <>
          {renderSkeletonPill({
            order: { xs: 2, sm: 3 },
            width: { xs: 80, sm: 104 },
          })}
          {renderSkeletonProgress({
            order: { xs: 3, sm: 2 },
            flexBasis: { xs: "100%", sm: "auto" },
          })}
        </>
      )}
    </Box>
  );

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <Stack spacing={0.5} sx={{ flexShrink: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: dashFont.cardHeading,
              fontWeight: 800,
              color: dashRef.heading,
            }}
          >
            {strings.DASHBOARD_RECENT_ACTIVITY_TITLE}
          </Typography>
          <Link
            component="button"
            type="button"
            aria-label="View all sent requests"
            onClick={() => history.push(viewpaths.sentSignRequestsViewPath)}
            sx={{
              fontSize: dashFont.link,
              fontWeight: 700,
              whiteSpace: "nowrap",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            View all
          </Link>
        </Stack>
        <Typography
          sx={{
            fontSize: dashFont.cardDescription,
            color: dashRef.metaText,
            // Tablet and phone references run the card title straight into the
            // rows — the sub-line is desktop-only chrome.
            display: { xs: "none", md: "block" },
          }}
        >
          {strings.DASHBOARD_RECENT_ACTIVITY_SUBTITLE}
        </Typography>
      </Stack>

      <Box
        aria-busy={loading}
        sx={{
          mt: { xs: 0.75, sm: 1, md: 2 },
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        {loading ? (
          <Stack>
            {Array.from({ length: visibleRows }, (_, index) =>
              renderSkeletonRow(index),
            )}
          </Stack>
        ) : visibleRequests.length > 0 ? (
          <Stack>{visibleRequests.map(renderRow)}</Stack>
        ) : (
          /* A dashboard panel — the medium size, one small action. The well
             itself draws no border by design (it normally sits inside a
             container that already has one), but this panel's own border sits
             one level further out, so the well needs its own here. */
          <EmptyState
            size="medium"
            icon="doc"
            title="No recent activity"
            body="Requests you send show up here as they move through signing."
            primaryAction={{
              label: "Create",
              onClick: () => {
                dispatch(setReduxSignRequest(null));
                history.push({
                  pathname: viewpaths.createSignRequestsViewPath,
                  search: "?type=others",
                  state: { isFileUploaded: false },
                });
              },
            }}
            sx={{
              border: `1px solid ${dashRef.cardBorder}`,
              borderRadius: "12px",
            }}
          />
        )}
      </Box>
    </Stack>
  );
};

export default DashboardRecentActivity;
